import React, { useEffect, useRef, useState } from 'react';
import { t } from './i18n';

type Slug = 'red-panda' | 'toucan' | 'axolotl' | 'fox' | 'parrot' | 'pufferfish' | 'rabbit' | 'owl' | 'cat' | 'penguin';
type Stage = 0 | 1 | 10 | 20 | 30;
type Card = { slug: Slug; stage: Stage };
// Habitat of each featured species (keeps neighbouring cards varied); names are not shown.
const species: Record<string, { habitat: string }> = {
  'red-panda': { habitat: 'land' }, toucan: { habitat: 'sky' },
  axolotl: { habitat: 'water' }, fox: { habitat: 'land' },
  parrot: { habitat: 'sky' }, pufferfish: { habitat: 'water' },
  rabbit: { habitat: 'land' }, owl: { habitat: 'sky' },
  cat: { habitat: 'land' }, penguin: { habitat: 'water' },
};
const featured: Card[] = [
  { slug: 'red-panda', stage: 30 }, { slug: 'fox', stage: 30 }, { slug: 'cat', stage: 30 },
  { slug: 'parrot', stage: 30 }, { slug: 'axolotl', stage: 30 }, { slug: 'penguin', stage: 30 },
  { slug: 'rabbit', stage: 20 }, { slug: 'toucan', stage: 20 }, { slug: 'pufferfish', stage: 20 },
  { slug: 'owl', stage: 10 }, { slug: 'fox', stage: 10 }, { slug: 'cat', stage: 1 },
  { slug: 'axolotl', stage: 0 }, { slug: 'pufferfish', stage: 0 }, { slug: 'penguin', stage: 0 },
];
const levels: Stage[] = [0, 1, 10, 20, 30];

function shuffleDeck(): Card[] {
  const cards = [...featured];
  if (Math.random() < .5) { cards[10] = { slug: 'fox', stage: 1 }; cards[11] = { slug: 'cat', stage: 1 }; }
  for (let attempt = 0; attempt < 2000; attempt++) {
    const deck = [...cards];
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    if (deck[0].stage !== 30 || ![1, 2, 3].some(i => deck[i].stage === 30)) continue;
    if (![8, 9, 10].some(i => deck[i].stage === 30)) continue;
    if (deck.every((item, index) => {
      const next = deck[(index + 1) % deck.length];
      const after = deck[(index + 2) % deck.length];
      return item.slug !== next.slug && !(item.stage === 0 && next.stage === 0)
        && !(species[item.slug].habitat === species[next.slug].habitat && species[item.slug].habitat === species[after.slug].habitat)
        && [0, 1, 2, 3, 4].some(i => deck[(index + i) % deck.length].stage === 30);
    })) return deck;
  }
  return cards;
}

function initWall(wall: HTMLElement, setPlaying: (playing: boolean) => void) {
  const deck = shuffleDeck();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const abort = new AbortController();
  const { signal } = abort;
  let paused = false, hovering = false, inView = false, lastFrame = performance.now(), frame = 0;
  const sync = () => setPlaying(!paused && !reduced.matches);
  class Rail {
    viewport: HTMLElement;
    track: HTMLElement;
    cards: HTMLElement[] = [];
    head: number;
    offset = 0;
    step = 1;
    drag: { id: number; lastX: number; startX: number; moved: boolean } | null = null;
    constructor(viewport: HTMLElement, index: number) {
      this.viewport = viewport;
      this.track = viewport.querySelector<HTMLElement>('.home-pet-rail-track')!;
      this.head = index ? 8 : 0;
      this.measure();
      this.shift(this.step * (index ? .5 : window.innerWidth <= 700 ? 1 : 0));
      viewport.addEventListener('pointerdown', e => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        this.drag = { id: e.pointerId, lastX: e.clientX, startX: e.clientX, moved: false };
        viewport.setPointerCapture(e.pointerId);
      }, { signal });
      viewport.addEventListener('pointermove', e => {
        if (!this.drag || e.pointerId !== this.drag.id) return;
        if (Math.abs(e.clientX - this.drag.startX) > 6) this.drag.moved = true;
        this.shift(this.drag.lastX - e.clientX);
        this.drag.lastX = e.clientX;
      }, { signal });
      const end = (e: PointerEvent) => {
        if (this.drag?.id !== e.pointerId) return;
        if (e.type === 'pointerup' && !this.drag.moved) {
          paused = !paused;
          sync();
        }
        this.drag = null;
      };
      viewport.addEventListener('pointerup', end, { signal });
      viewport.addEventListener('pointercancel', end, { signal });
      viewport.addEventListener('lostpointercapture', end, { signal });
      viewport.addEventListener('keydown', e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          paused = !paused;
          sync();
          return;
        }
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        this.shift(e.key === 'ArrowRight' ? this.step : -this.step);
      }, { signal });
    }
    item(position: number) { return deck[(position % deck.length + deck.length) % deck.length]; }
    fill(card: HTMLElement, position: number) {
      const { slug, stage } = this.item(position);
      card.dataset.species = slug;
      card.dataset.stage = String(stage);
      card.dataset.habitat = species[slug].habitat;
      card.style.setProperty('--tilt', `${[-1.3, .6, -1, .8, -.5][levels.indexOf(stage)]}deg`);
      card.innerHTML = `<div class="home-pet-card-face"><img src="/assets/forest-ui/marketing/pet-cards/${slug}-${stage}.webp" alt="" loading="lazy" decoding="async" draggable="false"/></div>`;
    }
    measure() {
      const style = getComputedStyle(this.viewport);
      const width = parseFloat(style.getPropertyValue('--card-w'));
      const gap = parseFloat(style.getPropertyValue('--card-gap'));
      this.step = width + gap;
      const needed = Math.ceil(this.viewport.clientWidth / this.step) + 2;
      while (this.cards.length < needed) {
        const card = document.createElement('div');
        card.className = 'home-pet-card';
        card.setAttribute('aria-hidden', 'true');
        this.fill(card, this.head + this.cards.length);
        this.track.append(card);
        this.cards.push(card);
      }
      this.paint();
    }
    paint() { this.track.style.transform = `translate3d(${-this.offset}px,0,0)`; }
    shift(distance: number) {
      if (!this.step || !distance) return;
      this.offset += distance;
      while (this.offset >= this.step) {
        this.offset -= this.step;
        const first = this.cards.shift()!;
        this.head = (this.head + 1) % deck.length;
        this.fill(first, this.head + this.cards.length);
        this.cards.push(first);
        this.track.append(first);
      }
      while (this.offset < 0) {
        this.offset += this.step;
        const last = this.cards.pop()!;
        this.head = (this.head - 1 + deck.length) % deck.length;
        this.fill(last, this.head);
        this.cards.unshift(last);
        this.track.prepend(last);
      }
      this.paint();
    }
  }
  const rails = [...wall.querySelectorAll<HTMLElement>('.home-pet-rail')].map((el, index) => new Rail(el, index));
  wall.addEventListener('mouseenter', () => { hovering = true; }, { signal });
  wall.addEventListener('mouseleave', () => { hovering = false; }, { signal });
  reduced.addEventListener('change', sync, { signal });
  window.addEventListener('resize', () => rails.forEach(rail => rail.measure()), { signal });
  const observer = new IntersectionObserver(entries => { inView = entries[0]?.isIntersecting ?? false; }, { threshold: .02 });
  observer.observe(wall);
  const animate = (now: number) => {
    const dt = Math.min(Math.max(now - lastFrame, 0), 80) / 1000;
    lastFrame = now;
    if (!paused && !reduced.matches && !hovering && inView && !document.hidden && rails.every(r => !r.drag)) rails.forEach(r => r.shift(18 * dt));
    frame = requestAnimationFrame(animate);
  };
  sync();
  frame = requestAnimationFrame(animate);
  return () => { cancelAnimationFrame(frame); observer.disconnect(); abort.abort(); };
}

export function HomePetCardWall() {
  const wall = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(true);
  useEffect(() => wall.current ? initWall(wall.current, setPlaying) : undefined, []);
  return <section className="home-pet-showcase" aria-labelledby="home-pet-showcase-title" data-home-module="M4-PETS">
    <div className="home-pet-showcase-copy"><p className="eyebrow">{t("一人一隻，陪孩子慢慢長大")}</p><div className="home-pet-showcase-heading"><h2 id="home-pet-showcase-title">{t("40 種寵物，")}<br /><em>{t("你會遇見哪一隻？")}</em></h2><p>{t("一人養一隻時，每個孩子隨機遇見一隻寵物。從專屬的蛋或小窩開始，集到積分就能看見牠第一次出場。")} {t("免費版可遇見其中 12 種，標準版 40 種全開。")}</p></div></div>
    <div className="home-pet-card-wall" ref={wall} aria-label={t("精選部分寵物的成長形態，以兩排卡牌緩慢橫向展示")}>
      {[0, 1].map(index => <div className="home-pet-rail" data-rail={index} key={index} tabIndex={0} role="region" aria-roledescription={t("輪播")} aria-label={playing ? t("第{row}排寵物卡牌，拖曳或方向鍵瀏覽，點按或按空白鍵暫停流動", { row: index + 1 }) : t("第{row}排寵物卡牌，拖曳或方向鍵瀏覽，點按或按空白鍵繼續流動", { row: index + 1 })}><div className="home-pet-rail-track" aria-hidden="true" /></div>)}
    </div>
  </section>;
}
